/**
 * One shared copy of a persisted value.
 *
 * `usePersistedState` keeps a copy per component and writes it back whenever the
 * component's effects run. A component inside a hidden tab keeps its old copy,
 * and when the tab is shown again React re-runs its effects and the old copy is
 * written over a newer one: a category added a moment ago vanished. A store
 * holds the value once, outside React. Components read it with
 * `useSyncExternalStore`, and only a real change writes.
 */

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

export type PersistedStore<T> = {
  get: () => T
  set: (next: T | ((current: T) => T)) => void
  subscribe: (listener: () => void) => () => void
}

export function createPersistedStore<T>(
  key: string,
  initial: () => T,
  options: {
    version?: number
    /** Read a value an older writer saved without the {v, d} envelope. */
    legacy?: (parsed: unknown) => T | undefined
  } = {},
): PersistedStore<T> {
  const version = options.version ?? 1
  const listeners = new Set<() => void>()
  let cache: { raw: string | null; value: T } | undefined
  let storageBound = false

  const notify = () => listeners.forEach((listener) => listener())

  const read = (): T => {
    const raw = getStorageItem(key)
    if (cache && cache.raw === raw) return cache.value
    let value: T | undefined
    if (raw != null) {
      try {
        const parsed = JSON.parse(raw) as { v?: number; d?: T }
        if (parsed && typeof parsed === "object" && parsed.v === version && "d" in parsed) {
          value = parsed.d
        } else if (options.legacy) {
          value = options.legacy(parsed)
        }
      } catch {
        value = undefined
      }
    }
    // An unreadable or missing value falls back to the default, kept stable
    // across calls until something is written.
    if (value === undefined) {
      value = cache && cache.raw === null && raw === null ? cache.value : initial()
    }
    cache = { raw, value }
    return value
  }

  const set: PersistedStore<T>["set"] = (next) => {
    const current = read()
    const value = typeof next === "function" ? (next as (c: T) => T)(current) : next
    if (Object.is(value, current)) return
    const payload = JSON.stringify({ v: version, d: value })
    cache = { raw: payload, value }
    setStorageItem(key, payload)
    notify()
  }

  const subscribe: PersistedStore<T>["subscribe"] = (listener) => {
    listeners.add(listener)
    if (!storageBound) {
      storageBound = true
      // Another tab, or a writer outside the store, changed the stored value.
      subscribeToStorageKey(key, () => {
        // Our own write already updated the cache and told the readers.
        if (cache && cache.raw === getStorageItem(key)) return
        cache = undefined
        notify()
      })
    }
    return () => {
      listeners.delete(listener)
    }
  }

  return { get: read, set, subscribe }
}

export function usePersistedStore<T>(
  store: PersistedStore<T>,
): readonly [T, PersistedStore<T>["set"]] {
  const value = React.useSyncExternalStore(store.subscribe, store.get, store.get)
  return [value, store.set] as const
}

/** The same shape without storage: one shared copy for this session. */
export function createMemoryStore<T>(initial: () => T): PersistedStore<T> {
  const listeners = new Set<() => void>()
  let value: T | undefined
  let ready = false
  const get = () => {
    if (!ready) {
      value = initial()
      ready = true
    }
    return value as T
  }
  return {
    get,
    set: (next) => {
      const current = get()
      const nextValue = typeof next === "function" ? (next as (c: T) => T)(current) : next
      if (Object.is(nextValue, current)) return
      value = nextValue
      listeners.forEach((listener) => listener())
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
